import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Search, UserRound, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { filterNav, NAV_ITEMS } from '@/lib/navigation'
import { useAuth } from '@/contexts/AuthContext'

interface SearchUser {
  id: number
  username: string
  displayName?: string
  email?: string
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { permissions } = useAuth()
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(query.trim()), 220)
    return () => window.clearTimeout(timer)
  }, [query])

  const destinations = useMemo(
    () =>
      filterNav(NAV_ITEMS, permissions).map((item) => ({
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
        group: item.section,
      })),
    [permissions],
  )

  const filteredDestinations = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return destinations.slice(0, 8)
    return destinations
      .filter((item) => `${item.label} ${item.group}`.toLowerCase().includes(term))
      .slice(0, 8)
  }, [destinations, query])

  const { data: userData, isFetching, isError } = useQuery({
    queryKey: ['command-user-search', searchTerm],
    queryFn: () =>
      api<{ users: SearchUser[] }>(`/api/ts/users/search?q=${encodeURIComponent(searchTerm)}`),
    enabled: open && searchTerm.length >= 2 && !!permissions?.viewReports,
    staleTime: 30_000,
  })

  const users = searchTerm === query.trim() ? userData?.users?.slice(0, 5) || [] : []
  const results = [
    ...filteredDestinations.map((item) => ({
      key: `nav-${item.id}`,
      label: item.label,
      detail: item.group,
      icon: item.icon,
      run: () => navigate(item.path),
    })),
    ...users.map((user) => ({
      key: `user-${user.id}`,
      label: user.displayName || `@${user.username}`,
      detail: `@${user.username}${user.email ? ` · ${user.email}` : ''}`,
      icon: UserRound,
      run: () => navigate(`/users?q=${encodeURIComponent(user.username)}`),
    })),
  ]

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open) return null

  function choose(index: number) {
    const result = results[index]
    if (!result) return
    result.run()
    close()
  }

  function close() {
    setQuery('')
    setActiveIndex(0)
    onClose()
    document.querySelector<HTMLButtonElement>('.admin-global-search')?.focus()
  }

  return (
    <div
      className="admin-command-backdrop fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[10vh] sm:pt-[15vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close()
      }}
    >
      <div
        className="admin-command-panel w-full max-w-[680px] overflow-hidden rounded-[24px] border border-[rgba(64,219,241,.2)]"
        role="dialog"
        aria-modal="true"
        aria-label="Admin search"
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.stopPropagation(); close() }
          if (event.key !== 'Tab') return
          const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled),input'))
          const first = items[0], last = items[items.length - 1]
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
        }}
      >
        <div className="flex items-center gap-3 border-b border-[rgba(64,219,241,.11)] px-5">
          <Search className="h-5 w-5 shrink-0 text-[#40dbf1]" />
          <input
            autoFocus
            aria-label="Search people and admin pages"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') close()
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                if (results.length) setActiveIndex((index) => Math.min(index + 1, results.length - 1))
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setActiveIndex((index) => Math.max(index - 1, 0))
              }
              if (event.key === 'Enter') {
                event.preventDefault()
                choose(activeIndex)
              }
            }}
            placeholder="Search people, pages, and workflows..."
            className="h-16 flex-1 bg-transparent text-base text-[#f0f4f8] outline-none placeholder:text-[#93a5ad]"
          />
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-2 text-[#93a5ad] transition-colors hover:bg-white/5 hover:text-[#f0f4f8]"
            aria-label="Close search"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[min(560px,65vh)] overflow-y-auto p-3">
          {isError && <p role="status" className="px-3 py-2 text-sm text-[#e3b766]">People search is unavailable. You can still open admin pages below.</p>}
          {results.length ? (
            <div className="space-y-1">
              {results.map((result, index) => {
                const Icon = result.icon
                return (
                  <button
                    key={result.key}
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(index)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all ${
                      index === activeIndex
                        ? 'bg-[rgba(64,219,241,.11)] text-[#f0f4f8] shadow-[inset_0_0_0_1px_rgba(64,219,241,.14)]'
                        : 'text-[#9aabbb] hover:bg-white/[.03]'
                    }`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(64,219,241,.08)] text-[#40dbf1]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{result.label}</span>
                      <span className="block truncate text-xs text-[#93a5ad]">{result.detail}</span>
                    </span>
                    <ArrowRight className={`h-4 w-4 ${index === activeIndex ? 'text-[#40dbf1]' : 'text-[#344454]'}`} />
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="px-6 py-14 text-center">
              <p className="text-sm font-semibold text-[#f0f4f8]">
                {isFetching ? 'Searching clyr...' : 'No matches found'}
              </p>
              <p className="mt-1 text-xs text-[#93a5ad]">Try a username, email, or admin destination.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[rgba(64,219,241,.09)] px-5 py-3 text-[10px] uppercase tracking-[.12em] text-[#93a5ad]">
          <span>{query.trim().length >= 2 ? 'Searching people + admin tools' : 'Type 2+ characters to search people'}</span>
          <span className="hidden sm:block">↑↓ navigate · enter open · esc close</span>
        </div>
      </div>
    </div>
  )
}
